// ForgotPassword.js
import React, { useState, useEffect } from "react";
import { Button, Form, Input, message, Card } from "antd";
import { ArrowLeft, Mail, Send, CheckCircle } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { API_METHODS } from "../../constants";
import useApi from "../../hooks/useApi";
import HeaderComponent from "./Home/HeaderComponent";
import Footer from "./Home/Footer";
import logo from "../../assets/logo.png";

const ForgotPassword = () => {
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [emailSent, setEmailSent] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");
  const navigate = useNavigate();

  // API hook
  const [forgotResponse, sendForgotRequest] = useApi();

  // Handle form submission
  const handleSubmit = (values) => {
    setSubmittedEmail(values.email);
    sendForgotRequest(
      { email: values.email },
      API_METHODS.POST,
      "/user/auth/forgot-password"
    );
  };

  // Handle API response
  useEffect(() => {
    if (forgotResponse.data && !forgotResponse.isLoading) {
      setEmailSent(true);
      messageApi.open({
        type: "success",
        content: "Password reset instructions sent to your email!",
        duration: 4,
      });
    } else if (forgotResponse.error) {
      messageApi.open({
        type: "error",
        content:
          forgotResponse.error ||
          "Failed to send reset email. Please try again.",
        duration: 4,
      });
    }
  }, [forgotResponse, messageApi]);

  const handleResendEmail = () => {
    if (submittedEmail) {
      sendForgotRequest(
        { email: submittedEmail },
        API_METHODS.POST,
        "/user/auth/forgot-password"
      );
    }
  };

  return (
    <>
      {contextHolder}
      <HeaderComponent />

      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 py-12 px-4">
        <div className="container mx-auto max-w-md">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <div className="w-12 h-12 rounded-full overflow-hidden">
                <img
                  src={logo}
                  alt="MahalaxmiPuja Logo"
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">
                  MahalaxmiPuja
                </h1>
                <p className="text-orange-600 font-medium text-sm">
                  Shree Mahalaxmi Ambabai, Kolhapur
                </p>
              </div>
            </div>
          </div>

          {!emailSent ? (
            // Forgot Password Form
            <Card className="rounded-2xl shadow-xl border border-orange-100">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-orange-100 to-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="h-8 w-8 text-orange-500" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">
                  Forgot Password?
                </h2>
                <p className="text-gray-600">
                  No worries! Enter your email and we'll send you reset
                  instructions.
                </p>
              </div>

              <Form form={form} onFinish={handleSubmit} layout="vertical">
                <Form.Item
                  label="Email Address"
                  name="email"
                  rules={[
                    {
                      required: true,
                      message: "Please enter your email address",
                    },
                    {
                      type: "email",
                      message: "Please enter a valid email address",
                    },
                  ]}
                >
                  <Input
                    size="large"
                    placeholder="Enter your registered email"
                    prefix={<Mail className="h-4 w-4 text-gray-400" />}
                  />
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    loading={forgotResponse.isLoading}
                    block
                    className="h-12 bg-gradient-to-r from-orange-500 to-red-500 border-none hover:from-orange-600 hover:to-red-600 font-medium"
                    icon={<Send className="h-4 w-4" />}
                  >
                    Send Reset Instructions
                  </Button>
                </Form.Item>
              </Form>

              <div className="text-center space-y-3">
                <div className="flex items-center justify-center space-x-4 text-sm">
                  <Link
                    to="/login"
                    className="flex items-center text-orange-600 hover:text-orange-700 font-medium"
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Back to Login
                  </Link>
                  <span className="text-gray-400">•</span>
                  <Link
                    to="/sign-up"
                    className="text-orange-600 hover:text-orange-700 font-medium"
                  >
                    Create Account
                  </Link>
                </div>
              </div>
            </Card>
          ) : (
            // Email Sent Success
            <Card className="rounded-2xl shadow-xl border border-orange-100">
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-green-100 to-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="h-8 w-8 text-green-500" />
                </div>

                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  Check Your Email
                </h2>

                <div className="mb-6">
                  <p className="text-gray-600 mb-4">
                    We've sent password reset instructions to:
                  </p>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                    <p className="font-medium text-green-800">
                      {submittedEmail}
                    </p>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <h3 className="font-semibold text-blue-800 mb-2">
                    Next Steps:
                  </h3>
                  <ol className="text-sm text-blue-700 space-y-1 text-left">
                    <li>1. Check your email inbox</li>
                    <li>2. Look for an email from MahalaxmiPuja</li>
                    <li>3. Click the reset password link</li>
                    <li>4. Create your new password</li>
                  </ol>
                </div>

                <div className="space-y-3">
                  <p className="text-sm text-gray-600">
                    Didn't receive the email? Check your spam folder.
                  </p>

                  <Button
                    onClick={handleResendEmail}
                    loading={forgotResponse.isLoading}
                    className="border-orange-500 text-orange-600 hover:bg-orange-50"
                  >
                    Resend Email
                  </Button>

                  <div className="text-center pt-4">
                    <Link
                      to="/login"
                      className="flex items-center justify-center text-orange-600 hover:text-orange-700 font-medium"
                    >
                      <ArrowLeft className="h-4 w-4 mr-1" />
                      Back to Login
                    </Link>
                  </div>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>

      <Footer />
    </>
  );
};

export default ForgotPassword;
